import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toFrench } from "@/utils/dateUtils";
import { usePaymentRemindersV2 } from "@/hooks/usePaymentRemindersV2";
import {
  Mail,
  Bell,
  FileText,
  DollarSign,
  Send,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Calendar,
  X,
  Settings,
  ExternalLink,
  Sparkles,
  Edit2,
} from "lucide-react";

export const PaymentReminders: React.FC = () => {
  const navigate = useNavigate();

  // ============================================
  // CUSTOM HOOK V2 (Nouveau système policies)
  // ============================================
  const {
    // Data
    invoices,
    quotes,
    statistics,
    quoteStatistics,
    reminderHistory,
    invoicePolicy,
    quotePolicy,

    // Loading
    isLoading: loading,
    isProcessing,

    // Actions
    sendManualInvoiceReminder,
    sendManualQuoteReminder,
    processInvoiceReminders,
    processQuoteReminders,
    loadInvoiceHistory,
    loadQuoteHistory,
    clearHistory,
  } = usePaymentRemindersV2();

  // ============================================
  // ÉTAT LOCAL UI UNIQUEMENT (pas de logique métier)
  // ============================================
  const [activeTab, setActiveTab] = useState<
    "invoices" | "quotes" | "settings" | "stats"
  >("invoices");
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<string | null>(null);

  // ============================================
  // HANDLERS UI (appellent le custom hook V2)
  // ============================================

  const handleLoadInvoiceHistory = async (invoiceId: string) => {
    const success = await loadInvoiceHistory(invoiceId);
    if (success) {
      setSelectedInvoice(invoiceId);
    }
  };

  const handleLoadQuoteHistory = async (quoteId: string) => {
    const success = await loadQuoteHistory(quoteId);
    if (success) {
      setSelectedQuote(quoteId);
    }
  };

  const handleCloseInvoiceHistory = () => {
    setSelectedInvoice(null);
    clearHistory();
  };

  const handleCloseQuoteHistory = () => {
    setSelectedQuote(null);
    clearHistory();
  };

  const formatDate = (dateString: string) => {
    return toFrench(dateString);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const getReminderLevelBadge = (level: string) => {
    const badges: Record<string, { color: string; text: string }> = {
      first: {
        color:
          "bg-teal-50 text-teal-700 border border-teal-200/50 shadow-sm",
        text: "1ère relance",
      },
      second: {
        color:
          "bg-gray-100 text-gray-700 border border-gray-200/50 shadow-sm",
        text: "2ème relance",
      },
      third: {
        color: "bg-red-50 text-red-700 border border-red-200/50 shadow-sm",
        text: "3ème relance",
      },
      manual: {
        color: "bg-green-50 text-green-700 border border-green-200/50 shadow-sm",
        text: "Manuelle",
      },
    };
    const badge = badges[level] || {
      color: "bg-gray-100 text-gray-800 border border-gray-200/50 shadow-sm",
      text: level,
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${badge.color}`}
      >
        {badge.text}
      </span>
    );
  };

  return (
    <div className="space-y-8">
        {/* Header - Volvo Style */}
        <div className="animate-fade-in-up">
          <h1 className="text-5xl font-light tracking-tight text-gray-900 mb-2 flex items-center gap-3">
            <Bell className="w-10 h-10 text-teal-600" />
            Relances de Paiement
          </h1>
          <p className="text-base text-gray-500 font-light tracking-wide">
            Gérez les relances automatiques pour vos factures et devis
          </p>
        </div>

        {/* Indicateur de politique active */}
        {(invoicePolicy || quotePolicy) && (
          <div
            className="bg-teal-50/80 backdrop-blur-sm border border-teal-200/50 text-teal-800 px-6 py-4 rounded-2xl shadow-lg animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
          >
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">
                Relances automatiques actives :
                {invoicePolicy && " Factures"}
                {invoicePolicy && quotePolicy && " •"}
                {quotePolicy && " Devis"}
              </span>
            </div>
          </div>
        )}

        {/* Tabs - Glassmorphic Buttons */}
        <div
          className="flex flex-wrap gap-3 animate-fade-in-up"
          style={{ animationDelay: "200ms" }}
        >
          <button
            onClick={() => setActiveTab("invoices")}
            className={`px-6 py-3 font-semibold rounded-2xl transition-all duration-300 shadow-sm flex items-center gap-2.5 ${
              activeTab === "invoices"
                ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg shadow-teal-500/30 scale-105"
                : "bg-white/60 backdrop-blur-sm text-gray-700 hover:bg-white border border-gray-300/50 hover:scale-105"
            }`}
          >
            <DollarSign className="w-4 h-4" />
            Factures ({invoices?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            className={`px-6 py-3 font-semibold rounded-2xl transition-all duration-300 shadow-sm flex items-center gap-2.5 ${
              activeTab === "quotes"
                ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg shadow-teal-500/30 scale-105"
                : "bg-white/60 backdrop-blur-sm text-gray-700 hover:bg-white border border-gray-300/50 hover:scale-105"
            }`}
          >
            <FileText className="w-4 h-4" />
            Devis ({quotes?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-6 py-3 font-semibold rounded-2xl transition-all duration-300 shadow-sm flex items-center gap-2.5 ${
              activeTab === "settings"
                ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg shadow-teal-500/30 scale-105"
                : "bg-white/60 backdrop-blur-sm text-gray-700 hover:bg-white border border-gray-300/50 hover:scale-105"
            }`}
          >
            <Edit2 className="w-4 h-4" />
            Paramètres
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`px-6 py-3 font-semibold rounded-2xl transition-all duration-300 shadow-sm flex items-center gap-2.5 ${
              activeTab === "stats"
                ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg shadow-teal-500/30 scale-105"
                : "bg-white/60 backdrop-blur-sm text-gray-700 hover:bg-white border border-gray-300/50 hover:scale-105"
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Statistiques
          </button>
        </div>

        {/* Contenu des onglets */}
        {activeTab === "invoices" && (
          <div
            className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 animate-fade-in-up"
            style={{
              animationDelay: "300ms",
              boxShadow:
                "0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)",
            }}
          >
            <div className="px-8 py-6 border-b border-gray-200/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-teal-600" />
                Factures nécessitant une relance
              </h2>
              <button
                onClick={processInvoiceReminders}
                disabled={loading || isProcessing || (invoices?.length || 0) === 0}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-6 py-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg shadow-teal-500/30 hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2.5"
              >
                <Send className="w-4 h-4" />
                {loading ? "Traitement..." : "Traiter toutes les relances"}
              </button>
            </div>

            <div className="overflow-x-auto">
              {(invoices?.length || 0) === 0 ? (
                <div className="px-8 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">
                      Aucune facture à relancer pour le moment
                    </p>
                  </div>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm border-b border-gray-200/50">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        N° Facture
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Échéance
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Retard
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Niveau
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Dernière relance
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/40 backdrop-blur-sm divide-y divide-gray-200/30">
                    {invoices.map((invoice) => (
                      <tr
                        key={invoice.invoiceId}
                        className="hover:bg-white/60 transition-colors duration-200"
                      >
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {invoice.clientName}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {invoice.clientEmail}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">
                          {formatAmount(invoice.total)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-700 font-medium">
                          {formatDate(invoice.dueDate)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-red-100 text-red-700 border border-red-200/50 shadow-sm">
                            <AlertTriangle className="w-3 h-3" />
                            {invoice.daysOverdue} jours
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm">
                          {getReminderLevelBadge(invoice.reminderLevel)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {invoice.lastReminderDate
                            ? formatDate(invoice.lastReminderDate)
                            : "-"}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() =>
                              handleLoadInvoiceHistory(invoice.invoiceId)
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl hover:bg-teal-100 hover:scale-110 transition-all duration-200 shadow-sm"
                          >
                            <Clock className="w-3 h-3" />
                            Historique
                          </button>
                          <button
                            onClick={() =>
                              sendManualInvoiceReminder(invoice.invoiceId)
                            }
                            disabled={loading || isProcessing}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-green-700 bg-green-50 rounded-xl hover:bg-green-100 hover:scale-110 transition-all duration-200 disabled:opacity-50 shadow-sm"
                          >
                            <Send className="w-3 h-3" />
                            Relancer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal historique - Glassmorphic */}
            {selectedInvoice && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden border border-white/40">
                  {/* Header - Teal Gradient Sticky */}
                  <div className="sticky top-0 px-8 py-6 bg-gradient-to-r from-teal-600 to-teal-700 border-b border-teal-500/30 backdrop-blur-xl z-10">
                    <div className="flex justify-between items-center">
                      <h3 className="text-2xl font-light text-white flex items-center gap-3 tracking-tight">
                        <Clock className="w-6 h-6" />
                        Historique des relances
                      </h3>
                      <button
                        onClick={handleCloseInvoiceHistory}
                        className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-8 overflow-y-auto max-h-[calc(85vh-100px)]">
                    {(reminderHistory?.length || 0) === 0 ? (
                      <div className="flex flex-col items-center gap-4 py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Mail className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium text-center">
                          Aucune relance envoyée
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reminderHistory.map((reminder) => (
                          <div
                            key={reminder.id}
                            className="bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-6 shadow-lg hover:bg-white/80 transition-all duration-200"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center gap-3">
                                {getReminderLevelBadge(reminder.reminderType)}
                                <span className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(reminder.sentAt)}
                                </span>
                              </div>
                              <span
                                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                                  reminder.status === "sent"
                                    ? "bg-green-100 text-green-800 border border-green-200/50"
                                    : "bg-red-100 text-red-800 border border-red-200/50"
                                }`}
                              >
                                {reminder.status === "sent"
                                  ? "✓ Envoyé"
                                  : "✗ Échec"}
                              </span>
                            </div>
                            <div className="text-sm space-y-2.5">
                              <div className="flex items-start gap-2">
                                <span className="font-bold text-gray-700 min-w-[80px]">
                                  À:
                                </span>
                                <span className="text-gray-600 font-medium">
                                  {reminder.sentTo}
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-bold text-gray-700 min-w-[80px]">
                                  Montant:
                                </span>
                                <span className="text-gray-900 font-bold">
                                  {formatAmount(reminder.amountDue)}
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-bold text-gray-700 min-w-[80px]">
                                  Retard:
                                </span>
                                <span className="text-red-600 font-bold">
                                  {reminder.daysOverdue} jours
                                </span>
                              </div>
                              <div className="mt-4 pt-4 border-t border-gray-200/50">
                                <span className="font-bold text-gray-700 block mb-2">
                                  Sujet:
                                </span>
                                <p className="text-gray-800 font-medium">
                                  {reminder.emailSubject}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "quotes" && (
          <div
            className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 animate-fade-in-up"
            style={{
              animationDelay: "300ms",
              boxShadow:
                "0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)",
            }}
          >
            <div className="px-8 py-6 border-b border-gray-200/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="w-6 h-6 text-teal-600" />
                Devis nécessitant une relance
              </h2>
              <button
                onClick={processQuoteReminders}
                disabled={loading || isProcessing || (quotes?.length || 0) === 0}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-6 py-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg shadow-teal-500/30 hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2.5"
              >
                <Send className="w-4 h-4" />
                {loading ? "Traitement..." : "Traiter toutes les relances"}
              </button>
            </div>

            <div className="overflow-x-auto">
              {(quotes?.length || 0) === 0 ? (
                <div className="px-8 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">
                      Aucun devis à relancer pour le moment
                    </p>
                  </div>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm border-b border-gray-200/50">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        N° Devis
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Créé le
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Expiration
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Niveau
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Dernière relance
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/40 backdrop-blur-sm divide-y divide-gray-200/30">
                    {quotes.map((quote) => (
                      <tr
                        key={quote.quoteId}
                        className="hover:bg-white/60 transition-colors duration-200"
                      >
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">
                          {quote.quoteNumber}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {quote.clientName}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {quote.clientEmail}
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-gray-900">
                          {formatAmount(quote.amountTotal)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="text-sm text-gray-700 font-medium">
                            {formatDate(quote.createdAt)}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {quote.daysSinceCreated} jours
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          {quote.expiryDate ? (
                            <>
                              <div className="text-sm text-gray-700 font-medium">
                                {formatDate(quote.expiryDate)}
                              </div>
                              <div
                                className={`text-xs font-medium ${
                                  quote.daysUntilExpiry &&
                                  quote.daysUntilExpiry < 0
                                    ? "text-red-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {quote.daysUntilExpiry !== null
                                  ? quote.daysUntilExpiry < 0
                                    ? `Expiré depuis ${Math.abs(
                                        quote.daysUntilExpiry
                                      )} jours`
                                    : `${quote.daysUntilExpiry} jours restants`
                                  : "-"}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm">
                          {getReminderLevelBadge(quote.nextReminderType)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {quote.lastReminderDate
                            ? formatDate(quote.lastReminderDate)
                            : "-"}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() =>
                              handleLoadQuoteHistory(quote.quoteId)
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl hover:bg-teal-100 hover:scale-110 transition-all duration-200 shadow-sm"
                          >
                            <Clock className="w-3 h-3" />
                            Historique
                          </button>
                          <button
                            onClick={() =>
                              sendManualQuoteReminder(quote.quoteId)
                            }
                            disabled={loading || isProcessing}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-green-700 bg-green-50 rounded-xl hover:bg-green-100 hover:scale-110 transition-all duration-200 disabled:opacity-50 shadow-sm"
                          >
                            <Send className="w-3 h-3" />
                            Relancer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal historique devis - Glassmorphic */}
            {selectedQuote && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden border border-white/40">
                  {/* Header - Teal Gradient Sticky */}
                  <div className="sticky top-0 px-8 py-6 bg-gradient-to-r from-teal-600 to-teal-700 border-b border-teal-500/30 backdrop-blur-xl z-10">
                    <div className="flex justify-between items-center">
                      <h3 className="text-2xl font-light text-white flex items-center gap-3 tracking-tight">
                        <Clock className="w-6 h-6" />
                        Historique des relances de devis
                      </h3>
                      <button
                        onClick={handleCloseQuoteHistory}
                        className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-8 overflow-y-auto max-h-[calc(85vh-100px)]">
                    {(reminderHistory?.length || 0) === 0 ? (
                      <div className="flex flex-col items-center gap-4 py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Mail className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium text-center">
                          Aucune relance envoyée
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reminderHistory.map((reminder) => (
                          <div
                            key={reminder.id}
                            className="bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-6 shadow-lg hover:bg-white/80 transition-all duration-200"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center gap-3">
                                {getReminderLevelBadge(reminder.reminderType)}
                                <span className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                                  <Calendar className="w-4 h-4" />
                                  {formatDate(reminder.sentAt)}
                                </span>
                              </div>
                              <span
                                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                                  reminder.status === "sent"
                                    ? "bg-green-100 text-green-800 border border-green-200/50"
                                    : "bg-red-100 text-red-800 border border-red-200/50"
                                }`}
                              >
                                {reminder.status === "sent"
                                  ? "✓ Envoyé"
                                  : "✗ Échec"}
                              </span>
                            </div>
                            <div className="text-sm space-y-2.5">
                              <div className="flex items-start gap-2">
                                <span className="font-bold text-gray-700 min-w-[80px]">
                                  À:
                                </span>
                                <span className="text-gray-600 font-medium">
                                  {reminder.sentTo}
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-bold text-gray-700 min-w-[80px]">
                                  Montant:
                                </span>
                                <span className="text-gray-900 font-bold">
                                  {formatAmount(reminder.amountDue)}
                                </span>
                              </div>
                              <div className="mt-4 pt-4 border-t border-gray-200/50">
                                <span className="font-bold text-gray-700 block mb-2">
                                  Sujet:
                                </span>
                                <p className="text-gray-800 font-medium">
                                  {reminder.emailSubject}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div
            className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-8 animate-fade-in-up"
            style={{
              animationDelay: "300ms",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)",
            }}
          >
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Settings className="w-10 h-10 text-teal-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Configuration des Politiques de Relance
              </h2>

              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Les politiques de relance sont maintenant gérées dans une interface dédiée
                avec des niveaux personnalisables et l'intégration ML pour des relances intelligentes.
              </p>

              {/* État actuel */}
              <div className="flex justify-center gap-4 mb-8">
                <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  invoicePolicy
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-gray-100 text-gray-500 border border-gray-200"
                }`}>
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Factures: {invoicePolicy ? "Actif" : "Inactif"}
                </div>
                <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  quotePolicy
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-gray-100 text-gray-500 border border-gray-200"
                }`}>
                  <FileText className="w-4 h-4 inline mr-1" />
                  Devis: {quotePolicy ? "Actif" : "Inactif"}
                </div>
              </div>

              <button
                onClick={() => navigate("/reminder-policies")}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-2xl font-semibold shadow-lg shadow-teal-500/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              >
                <Settings className="w-5 h-5" />
                Gérer les Politiques de Relance
                <ExternalLink className="w-4 h-4" />
              </button>

              {/* Info ML */}
              <div className="mt-8 p-4 bg-teal-50/80 border border-teal-200/50 rounded-2xl max-w-lg mx-auto">
                <div className="flex items-start gap-3 text-left">
                  <Sparkles className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-teal-800">
                      Relances Intelligentes (Bientôt)
                    </p>
                    <p className="text-xs text-teal-600 mt-1">
                      L'IA analysera l'historique de paiement de chaque client pour
                      optimiser le timing et le contenu des relances automatiquement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NOTE: Ancienne section settings (640 lignes) supprimée.
            Le nouveau système utilise /reminder-policies avec ReminderPolicy (JSON levels)
            qui permet des niveaux flexibles et l'intégration ML future */}


        {activeTab === "stats" && (
          <div className="space-y-6">

            {/* KPI Cards — Factures */}
            <div>
              <h2 className="text-2xl font-light tracking-tight text-gray-900 flex items-center gap-3 mb-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                Relances factures
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total relances */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <Send className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Total relances</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics?.totalReminders ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-1">{statistics?.uniqueInvoices ?? 0} factures uniques</p>
                </div>

                {/* Taux de succès */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-green-50 to-green-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Taux de succès</p>
                  <p className="text-2xl font-bold text-green-600">
                    {statistics && statistics.totalReminders > 0
                      ? Math.round((statistics.sentCount / statistics.totalReminders) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{statistics?.sentCount ?? 0} envoyés</p>
                </div>

                {/* Échecs */}
                <div className={`group backdrop-blur-xl rounded-3xl p-5 hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 ${
                  (statistics?.failedCount ?? 0) > 0
                    ? 'bg-red-50/30 border border-red-200/50 hover:bg-red-50/60 hover:shadow-2xl'
                    : 'bg-white/20 border border-white/30 hover:bg-white/70 hover:shadow-2xl'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2.5 rounded-2xl shadow-sm group-hover:shadow-md transition-shadow duration-300 ${
                      (statistics?.failedCount ?? 0) > 0
                        ? 'bg-gradient-to-br from-red-50 to-red-100/50'
                        : 'bg-gradient-to-br from-gray-50 to-gray-100/50'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${(statistics?.failedCount ?? 0) > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Échecs</p>
                  <p className={`text-2xl font-bold ${(statistics?.failedCount ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {statistics?.failedCount ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">emails non délivrés</p>
                </div>

                {/* Montant relancé */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <DollarSign className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Montant relancé</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatAmount(statistics?.totalAmountReminded ?? 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">total TTC</p>
                </div>
              </div>
            </div>

            {/* Répartition par niveau — Factures */}
            {statistics && (
              <div className="group bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl transition-all duration-500">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-teal-600" />
                  Répartition par niveau — Factures
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(() => {
                    const levels = [
                      { label: '1ère relance', value: statistics.firstReminders },
                      { label: '2ème relance', value: statistics.secondReminders },
                      { label: '3ème relance', value: statistics.thirdReminders },
                      { label: 'Manuelles', value: statistics.manualReminders },
                    ];
                    return levels.map(({ label, value }, index) => {
                      const pct = statistics.totalReminders > 0 ? Math.round((value / statistics.totalReminders) * 100) : 0;
                      const isThird = index === 2;
                      return (
                        <div key={label} className={`relative overflow-hidden rounded-2xl border p-4 transition-all duration-300 hover:shadow-lg ${
                          isThird && value > 0
                            ? 'bg-red-50/50 border-red-200/50'
                            : 'bg-teal-50/50 border-teal-200/50'
                        }`}>
                          <div
                            className={`absolute inset-0 ${isThird && value > 0 ? 'bg-red-100/40' : 'bg-teal-100/40'}`}
                            style={{ width: `${pct}%` }}
                          />
                          <div className="relative">
                            <p className={`text-sm font-medium ${isThird && value > 0 ? 'text-red-700' : 'text-teal-700'}`}>{label}</p>
                            <div className="flex items-baseline gap-2 mt-1">
                              <span className={`text-2xl font-bold ${isThird && value > 0 ? 'text-red-700' : 'text-teal-700'}`}>{value}</span>
                              <span className={`text-xs ${isThird && value > 0 ? 'text-red-500' : 'text-teal-500'}`}>{pct}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* KPI Cards — Devis */}
            <div>
              <h2 className="text-2xl font-light tracking-tight text-gray-900 flex items-center gap-3 mb-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                Relances devis
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total relances devis */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <Send className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Total relances</p>
                  <p className="text-2xl font-bold text-gray-900">{quoteStatistics?.totalReminders ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-1">{quoteStatistics?.uniqueQuotes ?? 0} devis uniques</p>
                </div>

                {/* Taux de succès devis */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-green-50 to-green-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Taux de succès</p>
                  <p className="text-2xl font-bold text-green-600">
                    {quoteStatistics && quoteStatistics.totalReminders > 0
                      ? Math.round((quoteStatistics.sentCount / quoteStatistics.totalReminders) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{quoteStatistics?.sentCount ?? 0} envoyés</p>
                </div>

                {/* Échecs devis */}
                <div className={`group backdrop-blur-xl rounded-3xl p-5 hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 ${
                  (quoteStatistics?.failedCount ?? 0) > 0
                    ? 'bg-red-50/30 border border-red-200/50 hover:bg-red-50/60 hover:shadow-2xl'
                    : 'bg-white/20 border border-white/30 hover:bg-white/70 hover:shadow-2xl'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2.5 rounded-2xl shadow-sm group-hover:shadow-md transition-shadow duration-300 ${
                      (quoteStatistics?.failedCount ?? 0) > 0
                        ? 'bg-gradient-to-br from-red-50 to-red-100/50'
                        : 'bg-gradient-to-br from-gray-50 to-gray-100/50'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${(quoteStatistics?.failedCount ?? 0) > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Échecs</p>
                  <p className={`text-2xl font-bold ${(quoteStatistics?.failedCount ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {quoteStatistics?.failedCount ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">emails non délivrés</p>
                </div>

                {/* Montant relancé devis */}
                <div className="group bg-white/20 backdrop-blur-xl rounded-3xl p-5 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 shadow-xl shadow-gray-300/10 border border-white/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <DollarSign className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">Montant relancé</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatAmount(quoteStatistics?.totalAmountReminded ?? 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">total TTC</p>
                </div>
              </div>
            </div>

            {/* Répartition par niveau — Devis */}
            {quoteStatistics && (
              <div className="group bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl transition-all duration-500">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-teal-600" />
                  Répartition par niveau — Devis
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(() => {
                    const levels = [
                      { label: '1ère relance', value: quoteStatistics.firstReminders || 0 },
                      { label: '2ème relance', value: quoteStatistics.secondReminders || 0 },
                      { label: '3ème relance', value: quoteStatistics.thirdReminders || 0 },
                      { label: 'Manuelles', value: quoteStatistics.manualReminders || 0 },
                    ];
                    const total = quoteStatistics.totalReminders || 1;
                    return levels.map(({ label, value }, index) => {
                      const pct = Math.round((value / total) * 100);
                      const isThird = index === 2;
                      return (
                        <div key={label} className={`relative overflow-hidden rounded-2xl border p-4 transition-all duration-300 hover:shadow-lg ${
                          isThird && value > 0
                            ? 'bg-red-50/50 border-red-200/50'
                            : 'bg-teal-50/50 border-teal-200/50'
                        }`}>
                          <div
                            className={`absolute inset-0 ${isThird && value > 0 ? 'bg-red-100/40' : 'bg-teal-100/40'}`}
                            style={{ width: `${pct}%` }}
                          />
                          <div className="relative">
                            <p className={`text-sm font-medium ${isThird && value > 0 ? 'text-red-700' : 'text-teal-700'}`}>{label}</p>
                            <div className="flex items-baseline gap-2 mt-1">
                              <span className={`text-2xl font-bold ${isThird && value > 0 ? 'text-red-700' : 'text-teal-700'}`}>{value}</span>
                              <span className={`text-xs ${isThird && value > 0 ? 'text-red-500' : 'text-teal-500'}`}>{pct}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Résumé global */}
            <div className="group bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 px-6 py-4 hover:bg-white/70 hover:shadow-2xl transition-all duration-500">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <p className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                  Total combiné : <span className="font-semibold text-gray-700">{(statistics?.totalReminders ?? 0) + (quoteStatistics?.totalReminders ?? 0)}</span> relances
                </p>
                <p>
                  Montant total : <span className="font-semibold text-gray-700">{formatAmount((statistics?.totalAmountReminded ?? 0) + (quoteStatistics?.totalAmountReminded ?? 0))}</span>
                </p>
              </div>
            </div>

          </div>
        )}
      </div>
  );
};
